import React, { useEffect, useState } from 'react';
import {
  CompaniesInfiniteSelectField,
  DeleteIcon,
  EditIcon,
  FloatingFormItem,
  Group,
  InputDate,
  PhoneFormField,
} from '@/components/_common';
import CustomersSelectField from '@/components/_common/CustomersSelectField';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import { CustomerVehicleReduced } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { Customer } from '@/core/domain/people/Customers/Customers.types';
import { ScheduleKanbanFormValues } from '@/core/domain/schedules/NewSchedules/NewSchedules.types';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDisclosure,
} from '@/hooks';
import {
  Avatar,
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  List,
  Modal,
  Row,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { FaCar, FaEdit, FaUserEdit, FaUserPlus } from 'react-icons/fa';
import VehicleSelectField from '@/components/_common/VehicleSelectField';
import CustomerSimplifiedRegisterModal from '@/components/Customers/CustomerSimplifiedRegisterModal';
import CustomerVehicleSimplifiedRegisterModal from '@/components/Customers/CustomerVehicleSimplifiedRegisterModal';
import { useGPStatusList } from '@/core/domain/visual-management/status/status.hooks';
import { GPStatusColor } from '@/core/domain/visual-management/status/status.types';
import {
  useActivityCreate,
  useActivityEdit,
  useActivityObservationCreate,
  useActivityObservationDelete,
  useActivityObservationEdit,
  useActivityObservationList,
} from '@/core/domain/visual-management/activities/activities.hooks';
import { getUserFromLocalStorage } from '@/utils/user';
import { SendOutlined } from '@ant-design/icons';
import { stringToHexColor } from '@/utils/functions/stringToHexColor';
import CheckIcon from '@/components/_common/CheckIcon';
import CloseIcon from '@/components/_common/CloseIcon';
import { isAppPortocar } from '@/index';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { removeMask } from '@/utils/mask';
import { DateTimeFormat } from '@/utils/moment';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { GPActivityDetail } from '@/core/domain/visual-management/activities/activities.types';

interface Props {
  companyIds?: number[];
  activityDetail?: GPActivityDetail;
  activityDetailDataUpdatedAt?: number;
  onCancel: VoidFunction;
  modalOpened?: boolean;
}

const KanbanScheduleForm: React.FC<Props> = ({
  companyIds,
  onCancel,
  modalOpened,
  activityDetail,
  activityDetailDataUpdatedAt,
}) => {
  const activityId = activityDetail?.id;

  const [form] = Form.useForm<ScheduleKanbanFormValues>();

  const { employeeId } = getUserFromLocalStorage();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const intl = useIntl();
  const [customerModalVisible, setCustomerModalAction] = useDisclosure();
  const [vehicleModalVisible, setVehicleModalAction] = useDisclosure();
  const vehicleId = Form.useWatch('vehicleId', form);
  const companyId = Form.useWatch('companyId', form);
  const customerId = Form.useWatch('customerId', form);
  const observationText = Form.useWatch('observation', form);

  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const [observationEditId, setObservationEditId] = useState<string>();

  const workOrderCompanyConfig =
    useWorkorderConfigurationCompanyQuery(companyId);
  const observationCreate = useActivityObservationCreate();
  const observationEdit = useActivityObservationEdit();
  const observationDelete = useActivityObservationDelete();
  const observationList = useActivityObservationList(activityId);
  const activityCreate = useActivityCreate();
  const activityEdit = useActivityEdit();

  const isLoading = activityCreate.isLoading || activityEdit.isLoading;

  const handleCustomerModalFinish = (customer: Customer) => {
    form.resetFields(['customerId']);
    form.setFieldsValue({ customerId: customer.id });
    setCustomerModalAction.close();
  };

  const handleVehicleModalFinish = async (vehicle: CustomerVehicleReduced) => {
    form.setFieldsValue({ vehicleId: vehicle.id });
  };

  const handleCreateObservation = async () => {
    if (!!activityId && !!observationText) {
      await observationCreate.mutateAsync({
        activityId,
        text: observationText,
        technicianId: Number(employeeId),
      });

      form.setFieldValue('observation', undefined);
    }
  };

  useEffect(() => {
    if (modalOpened && activityDetail) {
      const activity = activityDetail;

      form.setFieldsValue({
        companyId: activity?.companyId,
        entryDate: activity?.entryDate ? moment(activity.entryDate) : undefined,
        estimatedLeaveDate: activity?.estimatedLeaveDate
          ? moment(activity.estimatedLeaveDate)
          : undefined,
        estimatorId: activity?.estimator.id,
        statusId: activity?.status?.id,
        customerId: activity.customerId,
        vehicleId: activity.vehicleId,
        activityId: activity.id,
        indicatedBy: activity.indicatedBy,
        contact: activity.contact,
      });
    }
  }, [activityDetailDataUpdatedAt, modalOpened]);

  useEffect(() => {
    if (companyIds?.length) {
      form.setFieldValue('companyIds', companyIds);
    }
  }, [companyIds]);

  const handleSubmit = async () => {
    const values = await form.validateFields();

    if (!!activityId) {
      await activityEdit.mutateAsync({
        ...values,
        activityId,
        entryDate: values.entryDate.format(DateTimeFormat),
        estimatedLeaveDate: values.estimatedLeaveDate.format(DateTimeFormat),
        contact: removeMask(values.contact),
      });
    } else {
      await activityCreate.mutateAsync({
        ...values,
        entryDate: values.entryDate.format(DateTimeFormat),
        estimatedLeaveDate: values.estimatedLeaveDate.format(DateTimeFormat),
        generateWorkOrder: !!values.generateWorkOrder,
        contact: removeMask(values.contact),
      });
    }

    handleClose();
  };

  const handleClose = () => {
    form.resetFields();
    onCancel();
  };

  const deleteObservation = async (observationId: string) => {
    if (!!activityId) {
      Modal.confirm({
        title: 'Remover observação',
        content: 'Ao excluir a observação não será mais possível restaura-la.',
        onOk: async () => {
          await observationDelete.mutateAsync({
            activityId,
            observationId,
          });
        },
        okType: 'danger',
        okText: 'Remover',
      });
    }
  };

  const editObservation = async (observationId: string) => {
    const observationTextEdit = form.getFieldValue(
      `observation-${observationId}`,
    );

    if (!!activityId) {
      await observationEdit.mutateAsync({
        activityId,
        text: observationTextEdit,
        technicianId: Number(employeeId),
        observationId,
      });

      setObservationEditId(undefined);
    }
  };

  const statusList = useGPStatusList();

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {isLoading && (
        <ModalLoadingOverlay
          messages={['Salvando dados...']}
          style={{ position: 'absolute', width: '100%', height: '100%' }}
        />
      )}

      <Form form={form}>
        <Form.Item name="activityId" hidden />
        <Form.Item name="observationId" hidden />

        <Row gutter={[12, 12]}>
          <Col span={24} md={12}>
            <FloatingFormItem
              label="Empresa"
              name="companyId"
              rules={[defaultEmptyFieldError]}
            >
              <CompaniesInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              label="Orçamentista"
              name="estimatorId"
              rules={[defaultEmptyFieldError]}
            >
              <EmployeesInfiniteSelectField
                queryFilter={{ estimator: true, companyId: companyId }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              label="Data de entrada"
              name="entryDate"
              initialValue={moment()}
              rules={[defaultEmptyFieldError]}
            >
              <InputDate showTime />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              label="Previsão de entrega"
              name="estimatedLeaveDate"
              rules={[defaultEmptyFieldError]}
            >
              <InputDate showTime />
            </FloatingFormItem>
          </Col>

          <>
            <Col span={24}>
              <Row gutter={16}>
                <Col span={21}>
                  <FloatingFormItem
                    name="customerId"
                    label="Cliente"
                    rules={[defaultEmptyFieldError]}
                  >
                    <CustomersSelectField
                      showCpfCnpj={true}
                      onClear={() => {
                        form.resetFields(['vehicleId', 'customerId']);
                      }}
                    />
                  </FloatingFormItem>
                </Col>

                <Col span={3} style={{ marginTop: '4px' }}>
                  <Tooltip
                    title={intl.formatMessage({
                      id: `workOrders.domain.complements.${
                        customerId ? 'customerEdit' : 'customerCreate'
                      }`,
                    })}
                  >
                    <Button
                      size="large"
                      icon={customerId ? <FaUserEdit /> : <FaUserPlus />}
                      onClick={setCustomerModalAction.open}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </Col>

            <Col span={24}>
              <Row gutter={16}>
                <Col span={21}>
                  <FloatingFormItem
                    name="vehicleId"
                    label={intl.formatMessage({
                      id: 'workOrders.domain.vehicle',
                    })}
                  >
                    <VehicleSelectField
                      customerId={customerId}
                      onVehicleSelect={(vehicle) => {
                        if (vehicle) {
                          form.setFields([
                            {
                              name: 'customerId',
                              value: vehicle.customerId,
                              errors: [],
                            },
                          ]);
                        }
                      }}
                      queryEnable={!!customerId && !vehicleId}
                    />
                  </FloatingFormItem>
                </Col>

                <Col span={3} style={{ marginTop: '4px' }}>
                  <Tooltip
                    title={intl.formatMessage({
                      id: `workOrders.domain.complements.${
                        vehicleId ? 'vehicleEdit' : 'vehicleCreate'
                      }`,
                    })}
                  >
                    <Button
                      size="large"
                      disabled={!customerId}
                      onClick={setVehicleModalAction.open}
                      icon={vehicleId ? <FaEdit /> : <FaCar />}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </Col>
          </>

          <Col span={24}>
            <FloatingFormItem name="statusId" label="Status">
              <Select
                options={statusList.data?.map((status) => ({
                  value: status.id,
                  label: (
                    <Group gap={8}>
                      <div
                        style={{
                          width: 15,
                          height: 15,
                          borderRadius: 4,
                          backgroundColor: GPStatusColor[status.color],
                        }}
                      />
                      <span style={{ fontWeight: 500 }}>{status.name}</span>
                    </Group>
                  ),
                }))}
              />
            </FloatingFormItem>
          </Col>

          {!!workOrderCompanyConfig.data?.seeIndicatedBy && (
            <Col span={24}>
              <Row gutter={[12, 12]}>
                <Col span={24} md={14}>
                  <FloatingFormItem
                    name="indicatedBy"
                    label="Corretor"
                    rules={[defaultMaxLenghtFieldError(100)]}
                  >
                    <Input
                      disabled={
                        !hasPermissions([
                          Authorities.MOVIMENTATION_WORKORDER_AUTHORIZED_EDIT_INDICATE_PHONE,
                        ]) || !!activityId
                      }
                    />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={10}>
                  <PhoneFormField
                    fieldName="contact"
                    label="Celular"
                    required={false}
                    disabled={
                      !hasPermissions([
                        Authorities.MOVIMENTATION_WORKORDER_AUTHORIZED_EDIT_INDICATE_PHONE,
                      ]) || !!activityId
                    }
                  />
                </Col>
              </Row>
            </Col>
          )}

          <Col span={24}>
            <Input.Group compact style={{ width: '100%' }} className="scroll">
              <FloatingFormItem
                name="observation"
                label={
                  !!activityId
                    ? 'Adicione uma observação...'
                    : 'Adicione observação inicial...'
                }
                style={{ width: !!activityId ? 'calc(100% - 48px)' : '100%' }}
              >
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  showCount
                  maxLength={300}
                />
              </FloatingFormItem>

              {!!activityId && (
                <Tooltip title="Adicionar Observação">
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    style={{ height: 48, width: 48 }}
                    loading={observationCreate.isLoading}
                    onClick={handleCreateObservation}
                  />
                </Tooltip>
              )}
            </Input.Group>

            {!!observationList.data?.length && (
              <div className="scroll">
                <List
                  className="scroll"
                  loading={observationEdit.isLoading}
                  itemLayout="horizontal"
                  bordered
                  dataSource={observationList.data}
                  style={{ maxHeight: 200, overflow: 'auto' }}
                  renderItem={(item) => {
                    const edit = observationEditId === item.id;

                    return (
                      <List.Item
                        actions={[
                          edit ? (
                            <CheckIcon
                              popover="Salvar"
                              disabled={observationEdit.isLoading}
                              onClick={() => editObservation(item.id)}
                            />
                          ) : (
                            <EditIcon
                              iconProps={{
                                onClick: () => setObservationEditId(item.id),
                              }}
                            />
                          ),
                          edit ? (
                            <CloseIcon
                              onClick={() => setObservationEditId(undefined)}
                            />
                          ) : (
                            <DeleteIcon
                              onClick={() => deleteObservation(item.id)}
                            />
                          ),
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              style={{
                                textTransform: 'uppercase',
                                background: stringToHexColor(
                                  `${item.employee.name}${item.employee.id}`,
                                ).background,
                                color: stringToHexColor(
                                  `${item.employee.name}${item.employee.id}`,
                                ).color,
                                fontWeight: 600,
                              }}
                            >
                              {item.employee.name
                                ?.slice(0, 2)
                                .toLocaleLowerCase()}
                            </Avatar>
                          }
                          title={
                            <Group gap={10}>
                              <Typography.Text strong>
                                {item.employee.name}
                              </Typography.Text>

                              <Typography.Text type="secondary">
                                {moment(item.createdAt).fromNow()}
                              </Typography.Text>
                            </Group>
                          }
                          description={
                            <>
                              {edit && (
                                <FloatingFormItem
                                  noStyle
                                  name={`observation-${item.id}`}
                                  label="Edite a observação..."
                                  initialValue={item.text}
                                >
                                  <Input.TextArea
                                    autoSize={{ minRows: 1, maxRows: 6 }}
                                    maxLength={300}
                                  />
                                </FloatingFormItem>
                              )}

                              {!edit && item.text}
                            </>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </div>
            )}
          </Col>

          {!activityId && !isAppPortocar && (
            <Col span={24}>
              <Form.Item
                name="generateWorkOrder"
                valuePropName="checked"
                initialValue={false}
              >
                <Checkbox>Gerar orçamento ao salvar</Checkbox>
              </Form.Item>
            </Col>
          )}

          <Col span={24}>
            <div
              style={{ gap: '10px', display: 'flex', justifyContent: 'end' }}
            >
              <Button disabled={isLoading} onClick={handleClose}>
                Cancelar
              </Button>

              <Button type="primary" onClick={handleSubmit} loading={isLoading}>
                {!!activityId ? 'Editar' : 'Criar'}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      <CustomerSimplifiedRegisterModal
        visible={customerModalVisible}
        handleVisible={setCustomerModalAction.close}
        customerId={customerId}
        onFinishCallback={handleCustomerModalFinish}
      />

      <CustomerVehicleSimplifiedRegisterModal
        visible={vehicleModalVisible}
        handleVisible={setVehicleModalAction.toggle}
        customerId={customerId}
        vehicleId={vehicleId}
        key={vehicleId}
        onFinish={handleVehicleModalFinish}
      />
    </div>
  );
};

export default KanbanScheduleForm;
